// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Deal, JacketType } from '@interfaces/deal.interfaces';
import { catchError, takeWhile } from 'rxjs/operators';
import { DealsService } from '@services/deals.service';
import { showFormErrors } from 'src/app/utils/form-utils';
import { ErrorService } from '../../services/error.service';

enum ViewState {
    success = 'success',
    confirm = 'confirm',
}

@Component({
    selector: 'app-delete-deal-modal',
    templateUrl: './delete-deal-modal.component.html',
    styleUrls: ['./delete-deal-modal.component.scss'],
})
export class DeleteDealModalComponent implements OnDestroy {
    deleteDealForm: FormGroup;
    disableDelete = false;
    private isComponentAlive = true;
    public dealId: number;
    public jacketTypeId: JacketType;
    public customerName = '';
    protected readonly JacketType = JacketType;
    public header: string;
    public message: string;
    private jacketRoute: 'deals' | 'documents' | 'storage' = 'deals';

    constructor(
        private dialogRef: MatDialogRef<DeleteDealModalComponent>,
        private readonly dealsService: DealsService,
        private readonly errorService: ErrorService,
        private readonly router: Router,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        dialogRef.disableClose = true;
        if (typeof this.data === 'number') {
            this.dealId = this.data;
        } else {
            if (data?.dealId) {
                this.dealId = data.dealId;
            }

            if (data?.customerName) {
                this.customerName = data.customerName;
            }

            switch (data?.jacketTypeId) {
                case JacketType.Document:
                    this.jacketRoute = 'documents';
                    break;
                case JacketType.Storage:
                    this.jacketRoute = 'storage';
                    break;
                case JacketType.Deal:
                default:
                    this.jacketRoute = 'deals';
                    break;
            }

            this.header = 'Delete Jacket';
            this.message = `Are you sure you want to delete the jacket for <strong>${this.customerName}</strong>? 
            The jacket will be held in the deleted queue for 60 days and can be restored during that time. After 60 days, it will be permanently deleted.`;

            this.jacketTypeId = data?.jacketTypeId;
            this.customerName = data?.customerName ?? '';
        }

        this.deleteDealForm = new FormGroup({
            reason: new FormControl('', [Validators.required]),
        });
    }

    deleteDealSubmit(group: FormGroup): void {
        if (this.deleteDealForm.status === 'INVALID') {
            showFormErrors(this.deleteDealForm.controls);
            return;
        }

        this.disableDelete = true;
        if (group.valid) {
            this.dealsService
                .deleteDeal$(this.dealId, group.value.reason, this.jacketTypeId)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((response) => {
                        if (response?.error?.statusCode === 450) {
                            response.error.title = response.error.name;
                            this.errorService.showError(response?.error);
                        } else {
                            this.errorService.showGenericError();
                        }

                        this.dialogRef.close();
                        throw response;
                    })
                )
                .subscribe((deal: Deal) => {
                    this.dealId = deal?.dealId;
                    this.close(true);
                });
        }
    }

    cancel(): void {
        this.dialogRef.close();
    }

    close(deleted: boolean): void {
        this.dialogRef.close(deleted);
        this.router.navigate(['/dashboard', this.jacketRoute]);
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
